// Which product this deployment is (D125 A, 25 Sep). One codebase, two deployments: Canvas, the customer's guided
// meeting at canvas.clikhome.in, and ClikMeet, Clikin's own meeting opened from ClikWork (area 25). STUDIO_PRODUCT
// says which; unset is Canvas, so the deployment that exists today did not have to change to stay what it is.
//
// ClikMeet is the room without Canvas's sales parts. Each feature below is a door the product keeps SHUT when it is
// false: the server refuses its routes and its fields (functions/_middleware.js, the create and people routes), and the
// page never offers it. A new Canvas feature is shut in ClikMeet until someone opens it here; ClikMeet's own (the room)
// is shut in Canvas the same way.
export const PRODUCTS = {
  canvas: {
    id: "canvas", name: "Walkin", by: "Clikin.ai", title: "Walkin · Clikin.ai",
    // Its addresses (Address 2, D762 A): the new one first, then the one links were sent on before 15 Oct. Both serve;
    // which one a new link is built on is PUBLIC_ORIGINS's first entry (lib/origin.js), never this list.
    hosts: ["walkin.clikin.ai", "canvas.clikhome.in"],
    // The guided tour, the site camera, live assist, recording, the code-and-last-four door, and Helix's settlement (a
    // booking's ref, and the outcome and ack Helix settles on). The machine API to create, move, cancel and change
    // people is not a feature: both products have it.
    features: { tour: true, site: true, assist: true, recording: true, phoneCode: true, helix: true, room: false, scribe: false },
  },
  clikmeet: {
    id: "clikmeet", name: "Talkin", by: "Clikin", title: "Talkin · Clikin",
    hosts: ["talkin.clikin.ai", "meet.clikwork.co"],
    // room (M3): one link for everyone, colleagues signed in by ClikWork and guests who knock. Canvas's own.
    // scribe (M4): the transcript listener's pass and ClikWork's read of a room's transcript.
    features: { tour: false, site: false, assist: false, recording: false, phoneCode: false, helix: false, room: true, scribe: true },
  },
};

// ClikMeet's own video recording (D137 A, D138 A) is OFF until its deployment says CLIKMEET_RECORDING=on — set once the
// private bucket and the scribe's recorder are in place. Off, nobody is offered Record and the studio refuses it, so a
// host never sees "Recording" while nothing is kept (gate the door you built).
export const videoRecordingOn = (env) => productOf(env).id === "clikmeet" && String(env?.CLIKMEET_RECORDING || "").trim().toLowerCase() === "on";

// A value that names no product is a broken deployment, never Canvas by default: the middleware answers every request
// with a 500 rather than serve a meeting under the wrong name and with the wrong doors open.
export function productOf(env) {
  const v = String(env?.STUDIO_PRODUCT ?? "").trim().toLowerCase();
  if (!v) return PRODUCTS.canvas;
  if (!Object.hasOwn(PRODUCTS, v)) throw new Error(`STUDIO_PRODUCT=${v} names no product`);
  return PRODUCTS[v];
}

// The paths that belong to one of Canvas's features. Matched on the canonical path (lower case, decoded, dot-segments
// resolved — the middleware's canonicalPath), so an encoded or doubled slash cannot walk round it.
const FEATURE_PATHS = [
  // Canvas's own files: the projects' scenes, the hero photos, and Canvas's mark. ClikMeet's mark (Ravi, 26 Sep: M2)
  // is under /icons/clikmeet/ and goes with ClikMeet's own feature, the room — so neither serves the other's.
  ["tour", /^\/(canvas|brand)(\/|$)/],
  ["tour", /^\/(icons(\/(?!clikmeet(\/|$))|$)|favicon\.ico$|site\.webmanifest$)/],
  ["room", /^\/icons\/clikmeet(\/|$)/],
  ["tour", /^\/api\/(book|events|summary|ink|still)$/],
  ["site", /^\/api\/site$/],
  ["site", /^\/api\/meetings\/[^/]+\/site$/],
  ["assist", /^\/api\/assist(\/|$)/],
  ["phoneCode", /^\/api\/code$/],
  ["helix", /^\/api\/meetings\/[^/]+\/(outcome|ack)$/],
  ["room", /^\/api\/(rooms|knocks|chat)(\/|$)/],
  // /api/lines is shared: Canvas's live assist and ClikMeet's scribe both send their words there (the meeting decides).
  // Only the PASS is ClikMeet's. /api/scribe/recording is open to both, because both record and both must report:
  // ClikMeet by its own switch (its `recording` feature is false and always has been — it records via the room's
  // metadata, not `meeting.recorded`), Canvas by D145. Gating the report on `scribe` shut it for Canvas; gating it
  // on `recording` would shut it for ClikMeet. It is the handler that is product-aware, not the path.
  ["scribe", /^\/api\/scribe\/join$/],
  ["scribe", /^\/api\/meetings\/[^/]+\/transcript$/],
];

// The feature a path belongs to that this product has switched off, or null when the path is open.
export function shutFeature(product, path) {
  for (const [feature, re] of FEATURE_PATHS) if (!product.features[feature] && re.test(path)) return feature;
  return null;
}

// What a create or a change of people may carry only where its feature is on. Returns the first field refused, or null.
export function shutField(product, input) {
  const f = product.features;
  // Each field is read the way lib/store.js reads it, so what passes here is what is stored as nothing: a switch is on
  // when truthy (Boolean(input.recorded)), a text is there when it is not blank once made a string (clip(), so false
  // is "false"). Audit 25 Sep: `ref: { site_visit_id: false }` passed a narrower test and was stored as ref "false".
  const text = (v) => String(v ?? "").trim() !== "";
  const brief = input?.brief && typeof input.brief === "object" && Object.values(input.brief).some(text);
  const checks = [
    ["canvas", f.tour, Boolean(input?.canvas)],
    ["brief", f.tour, Boolean(brief)],
    ["assist", f.assist, Boolean(input?.assist)],
    ["recorded", f.recording, Boolean(input?.recorded)],
    ["customer.phone", f.phoneCode, text(input?.customer?.phone)],
    ["ref", f.helix, text(input?.ref?.site_visit_id)],
    // A Huddle's close and its guests' addresses (D146 A, D147 A) are the room's.
    ["closesInMin", f.room, input?.closesInMin != null && input.closesInMin !== ""],
    ["guests.email", f.room, Array.isArray(input?.guests) && input.guests.some((g) => text(g?.email))],
  ];
  for (const [field, on, asked] of checks) if (asked && !on) return field;
  return null;
}

// shutField as the error the create and people routes answer: 400 `not_in_product`, naming the field.
export function refuseShut(product, input) {
  const field = shutField(product, input);
  if (field) throw Object.assign(new Error(`${field} is not part of ${product.name}`), { status: 400, code: "not_in_product" });
}

// ClikMeet's head (Ravi, 26 Sep: M2 + P1): its mark from the brand repo's clikmeet/ (copies in public/icons/clikmeet/,
// pinned by tests/unit/brand.test.mjs), its manifest, and the room link's preview for WhatsApp and Gmail. Every URL
// carries its file's hash (node scripts/stamp-icons.mjs rewrites them here too).
const CLIKMEET_HEAD = [
  '<meta name="theme-color" content="#0B1220">',
  '<link rel="icon" href="/icons/clikmeet/favicon.ico?v=1d9ceb1c0c" sizes="32x32">',
  '<link rel="icon" type="image/svg+xml" href="/icons/clikmeet/favicon.svg?v=880ac8ed29">',
  '<link rel="apple-touch-icon" href="/icons/clikmeet/apple-touch-icon.png?v=d8e139134b">',
  '<link rel="manifest" href="/icons/clikmeet/site.webmanifest?v=50a593682f">',
  '<meta property="og:type" content="website">',
  '<meta property="og:site_name" content="Talkin · Clikin">',
  '<meta property="og:title" content="Talkin · Clikin">',
  '<meta property="og:description" content="A video meeting with Clikin. Open the link to join — no app needed.">',
  '<meta property="og:image" content="https://meet.clikwork.co/icons/clikmeet/og-1200x630.png?v=dc30541b64">',
  '<meta property="og:image:width" content="1200">',
  '<meta property="og:image:height" content="630">',
  '<meta name="twitter:card" content="summary_large_image">',
];

// The page's head for this product. Canvas's is the file as built. ClikMeet's names ClikMeet, drops Canvas's mark,
// share card and theme colour, carries its own (CLIKMEET_HEAD), and tells the script which product it is on
// <html data-product>.
export function productPage(html, product) {
  if (product.id === "canvas") return html;
  const esc = (s) => s.replace(/[&<>"]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" })[c]);
  const drop = /^\s*(<!--[\s\S]*?-->|<link rel="(icon|apple-touch-icon|manifest)"[^>]*>|<meta (property="og:|name="twitter:|name="theme-color")[^>]*>)\s*$/;
  // A comment can run over several lines: take them out whole before the line-by-line pass.
  const lines = html.replace(/[ \t]*<!--[\s\S]*?-->\n?/g, "").split("\n").filter((l) => !drop.test(l));
  return lines.join("\n")
    .replace(/<html lang="en">/, `<html lang="en" data-product="${esc(product.id)}">`)
    .replace(/<title>[^<]*<\/title>/, `<title>${esc(product.title)}</title>`)
    .replace(/<\/title>\n/, `</title>\n${product.id === "clikmeet" ? CLIKMEET_HEAD.map((t) => `  ${t}\n`).join("") : ""}`);
}
